import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useGetChoices, useGetSystemType } from "../../hooks";
import { FilterState } from "../../types";

interface SystemsFilterProps {
  currentFilters?: FilterState & { search: string };
  onApplyFilters: (filters: any) => void;
  onResetFilters: () => void;
}

export const SystemsFilter: React.FC<SystemsFilterProps> = ({
  currentFilters = {
    search: "",
    systemType: "",
    status: null,
    criticality: "",
    dateRange: { from: "", to: "" },
  },
  onApplyFilters,
  onResetFilters,
}) => {
  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    systemType: string;
    status: string;
    criticality: string;
    dateFrom: string;
    dateTo: string;
  }>({
    systemType: currentFilters?.systemType || "all",
    status: currentFilters?.status || "all",
    criticality: currentFilters?.criticality || "all",
    dateFrom: currentFilters?.dateRange?.from || "",
    dateTo: currentFilters?.dateRange?.to || "",
  });

  // Fetch choice data
  const { data: systemTypesData = [] } = useGetSystemType("system_type", true);
  const { data: statusData } = useGetChoices("system_status");
  const { data: criticalityData } = useGetChoices("criticality_level");

  // Format options for dropdowns
  const systemTypeOptions = useMemo(() => {
    const options = [{ label: "All System Types", value: "all" }];
    if (systemTypesData?.length > 0) {
      options.push(
        ...systemTypesData.map((type: any) => ({
          value: type.code,
          label: type.name,
        }))
      );
    }
    return options;
  }, [systemTypesData]);

  const statusOptions = useMemo(() => {
    const options = [{ label: "All Statuses", value: "all" }];
    if (statusData) {
      options.push(
        ...statusData.map((status) => ({
          label: status.value,
          value: String(status.key),
        }))
      );
    }
    return options;
  }, [statusData]);

  const criticalityOptions = useMemo(() => {
    const options = [{ label: "All Criticality Levels", value: "all" }];
    if (criticalityData) {
      options.push(
        ...criticalityData.map((level) => ({
          label: level.value,
          value: level.value,
        }))
      );
    }
    return options;
  }, [criticalityData]);

  // Create form fields
  const createFormFields = (): FormField[] => [
    {
      name: "systemType",
      type: "select",
      label: "System Type",
      required: false,
      options: systemTypeOptions,
      placeholder: "Select system type",
      classes: FIELD_CLASSES,
    },
    {
      name: "status",
      type: "select",
      label: "Status",
      required: false,
      options: statusOptions,
      placeholder: "Select status",
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: createFormFields(),
    defaultValues: tempFilters,
  });

  // Update form values when current filters change
  useEffect(() => {
    const newTempFilters = {
      systemType: currentFilters?.systemType || "all",
      status: currentFilters?.status ? String(currentFilters.status) : "all",
      criticality: currentFilters?.criticality || "all",
      dateFrom: currentFilters?.dateRange?.from || "",
      dateTo: currentFilters?.dateRange?.to || "",
    };

    setTempFilters(newTempFilters);
    form.reset(newTempFilters);
  }, [currentFilters, form]);

  // Handle form submission - format data for the parent component
  const handleSubmit = (data: any) => {
    const filtersToApply: any = {
      systemType:
        data.systemType && data.systemType !== "all" ? [data.systemType] : [],
      status: data.status && data.status !== "all" ? [Number(data.status)] : [],
      criticality:
        data.criticality && data.criticality !== "all"
          ? [data.criticality]
          : [],
      dateRange: {
        from: data.dateFrom || "",
        to: data.dateTo || "",
      },
    };

    onApplyFilters(filtersToApply);
  };

  // Handle reset
  const handleReset = () => {
    const defaultFilters = {
      systemType: "all",
      status: "all",
      criticality: "all",
      dateFrom: "",
      dateTo: "",
    };
    setTempFilters(defaultFilters);
    form.reset(defaultFilters);
    onResetFilters();
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Custom footer with Apply and Reset buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-3 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleReset}>
        Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onFieldChange={handleFieldChange}
      config={{
        showSubmitButton: false, // We're using custom footer
        showResetButton: false, // We're using custom footer
        layout: "vertical",
        spacing: "medium",
      }}
      gridColumns={3}
      responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
      className="space-y-6"
      renderCustomFooter={renderCustomFooter}
    />
  );
};
